import {Component, HostBinding, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import { ReportPageInterface } from 'src/app/interface';
import { FieldValue, IncomeStatement, Report } from 'src/app/model';
import { EntityType, FinancialType, FooterButtonOption, IncomeStatementType, PageNumbers } from 'src/app/model/Enums';
import { ReportProvider } from 'src/app/providers';
import { ReportService, ToasterService } from 'src/app/services';
import { radioGroupOption } from '../../commonModel/common-classes';
import {routeTransition} from '../../../../animations/route-transition';

@Component({
  selector: 'app-future-projections',
  templateUrl: './future-projections.component.html',
  styleUrls: ['./future-projections.component.scss'],
  animations: [routeTransition],
})
export class FutureProjectionsComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  @ViewChild('futureProjectionsForm') futureProjectionsForm: NgForm;

  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  indicationOption: radioGroupOption[] = [];
  fpIst: IncomeStatement[] = [];

  isTypeEnum = IncomeStatementType;
  isForm1065: boolean = false;

  constructor(public reportService: ReportService,
    private reportProvider: ReportProvider,
    private router: Router,
    private toastr: ToasterService) { }

  ngOnInit(): void {

    this.indicationOption.push({ title: 'Yes', value: true });
    this.indicationOption.push({ title: 'No', value: false });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport?.LATEST?.INCOME_STATEMENT?.length) {
        this.isForm1065 = this.currentReport.LATEST.COMPANY.ENTITY_TYPE == EntityType.FORM_1065;

        this.currentReport.LATEST.INCOME_STATEMENT.forEach(element => {
          if (element.IS_TYPE != IncomeStatementType.FORECASTED && element.IS_TYPE != IncomeStatementType.INTERIM) {
            this.fpIst.push(element);
          }
        });
        this.fpIst.sort((a, b) => Number(a.YEAR) - Number(b.YEAR));

        if (this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.IS_TYPE == IncomeStatementType.FORECASTED)) {
          this.fpIst.push(this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.IS_TYPE == IncomeStatementType.FORECASTED));
        }
      }
    });

  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description field update API
   */
   callUpdateVersionDetailsAPI(fieldName: string) {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'REPORT_VERSION';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = this.currentReport.LATEST[fieldName];
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description for get total of column
   * @param obj income statement object
   * @returns total of column
   */
   calculateTotal(obj: IncomeStatement) {
    if (!obj)
      return 0;
    return Number(obj.PROFIT ? obj.PROFIT.toString().replace(/,/g, '') : 0) +
      Number(obj.INTEREST ? obj.INTEREST.toString().replace(/,/g, '') : 0) +
      Number(obj.DEPRECIATION ? obj.DEPRECIATION.toString().replace(/,/g, '') : 0) +
      Number(obj.AMORTIZATION ? obj.AMORTIZATION.toString().replace(/,/g, '') : 0) +
      Number(obj.OFFICER_COMP ? obj.OFFICER_COMP.toString().replace(/,/g, '') : 0) +
      (this.isForm1065 ? Number(obj.GUARANTEED_PAYMENTS ? obj.GUARANTEED_PAYMENTS.toString().replace(/,/g, '') : 0) : 0);
  }

  /**
   * @description for return seleted month
   * @param is income statement object
   * @returns return selected month
   */
   getSelectedMonth(is: IncomeStatement) {
    return is.MONTHS || 1;
  }

  /**
   * @description for get last histotical revenue
   * @returns last historical revenue
   */
   getLastHistoricalRevenue() {
    const interimYear = this.fpIst.filter(el => el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE && el.FINANCIAL_TYPE != FinancialType.NOT_AVAILABLE);
    return interimYear[interimYear.length - 1] ? interimYear[interimYear.length - 1].REVENUE : 0;
  }

  /**
   * @description for get last histotical year month
   * @returns last historical month
   */
  getLastHistoricalMonth() {
    const interimYear = this.fpIst.filter(el => el.IS_TYPE == IncomeStatementType.HISTORICAL && el.FINANCIAL_TYPE && el.FINANCIAL_TYPE != FinancialType.NOT_AVAILABLE);
    return interimYear[interimYear.length - 1] ? interimYear[interimYear.length - 1].MONTHS : 1;
  }

  /**
   * @description get previous year revenue
   * @param index position
   * @returns return previous year revenue
   */
   getPreviousYearRevenue(index: number) {
    if (index === 0)
      return 0;
    return this.fpIst?.[index - 1].REVENUE || 0;
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return !this.futureProjectionsForm.invalid; // && this.currentReport.LATEST.INCOME_STATEMENT.length > 1;
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return false;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK) {
      this.reportService.updateMaxStage(PageNumbers.FUTURE_PROJECTIONS);
      if (this.reportService.showRentAdjustments()) {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/rent/rent-details`);
      } else if (this.reportService.showOwnershipSalary() == 1) {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/salary/manager-salary`);
      } else if (this.reportService.showOwnershipSalary() == 2) {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/salary/replacement-salary`);
      } else {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/adjustments`);
      }
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/income`);
    }
  }

}
